import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit, Trash2, RefreshCw, UserPlus, Key, Settings, Save, X } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import UserModal from '../components/modals/UserModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';
import { useToast } from '../hooks/useToast';
import PasswordResetModal from '../components/modals/PasswordResetModal';

interface UserWithRole {
  id: string;
  email: string;
  role: string;
  created_at: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [userToReset, setUserToReset] = useState<UserWithRole | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<string>('');
  
  const { isAdmin, userRole } = useAuth();
  const navigate = useNavigate();
  const { toast, ToastComponent } = useToast();

  // Redirect if not admin
  useEffect(() => {
    if (userRole && !isAdmin) {
      navigate('/');
    }
  }, [userRole, isAdmin, navigate]);

  // Fetch users directly from the database
  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Fetching users from safe_users table...');
      
      const { data, error } = await supabase
        .from('safe_users')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }
      
      console.log('Users fetched successfully:', data?.length || 0);
      setUsers(data || []);
      
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError(error.message || 'Error al cargar los usuarios');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to create user
  const createUser = async (email: string, role: string, password: string) => {
    try {
      // Generate a UUID for the new user
      const userId = crypto.randomUUID();
      
      // Insert into safe_users table with generated UUID
      const { data, error } = await supabase
        .from('safe_users')
        .insert({
          id: userId,
          email: email,
          role: role
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error inserting user:', error);
        throw new Error(`Error al crear el usuario: ${error.message}`);
      }
      
      console.log('User created successfully:', data);
      return data;
    } catch (error) {
      console.error('Error in createUser:', error);
      throw error;
    }
  };

  // Function to update user role directly in the database
  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      // Use hybrid RPC function to update user role in auth metadata
      const { data, error } = await supabase.rpc('update_user_role_hybrid', {
        user_id: userId,
        new_role: newRole
      });
      
      if (error) {
        throw new Error(`Error updating user role: ${error.message}`);
      }
      
      return true;
    } catch (error) {
      throw error;
    }
  };

  // Function to delete user
  const deleteUser = async (userId: string) => {
    try {
      // Delete only from safe_users table
      const { error: deleteError } = await supabase
        .from('safe_users')
        .delete()
        .eq('id', userId);
      
      if (deleteError) throw deleteError;
      
      return true;
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: UserWithRole) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteUser = (userId: string) => {
    setUserToDelete(userId);
    setIsDeleteModalOpen(true);
  };

  const handleResetPassword = (user: UserWithRole) => {
    setUserToReset(user);
    setIsPasswordResetModalOpen(true);
  };

  // Start inline editing
  const startEditRole = (user: UserWithRole) => {
    setEditingUser(user.id);
    setEditingRole(user.role);
  };

  // Cancel inline editing
  const cancelEditRole = () => {
    setEditingUser(null);
    setEditingRole('');
  };

  // Save inline editing
  const saveEditRole = async (userId: string) => {
    if (!editingRole) return;
    
    setIsLoading(true);
    try {
      await updateUserRole(userId, editingRole);
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: editingRole } : user
      ));
      
      toast.success('Rol actualizado correctamente');
      setEditingUser(null);
      setEditingRole('');
    } catch (error: any) {
      console.error('Error updating user role:', error);
      setError(error.message || 'Error al actualizar el rol del usuario');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setIsLoading(true);
    try {
      await deleteUser(userToDelete);
      
      // Update local state
      setUsers(prev => prev.filter(user => user.id !== userToDelete));
      toast.success('Usuario eliminado correctamente');
      
      // Close modal and reset state
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError(error.message || 'Error al eliminar el usuario');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserSaved = (newUser: UserWithRole) => {
    // Update local state
    setUsers(prev => {
      const existingIndex = prev.findIndex(user => user.id === newUser.id);
      if (existingIndex >= 0) {
        // Update existing user
        const updatedUsers = [...prev];
        updatedUsers[existingIndex] = newUser;
        return updatedUsers;
      } else {
        // Add new user
        return [...prev, newUser];
      }
    });
    
    setIsModalOpen(false);
    setSelectedUser(null);
    toast.success(
      selectedUser ? 'Usuario actualizado' : 'Usuario creado', 
      selectedUser ? 'El usuario ha sido actualizado correctamente' : 'El usuario ha sido creado correctamente'
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Gestión de Usuarios</h2>
        <div className="flex gap-2">
          <Link to="/roles-permisos">
            <Button
              variant="outline"
              icon={<Settings className="h-5 w-5" />}
            >
              Gestionar Roles
            </Button>
          </Link>
          <Button
            variant="outline"
            icon={<RefreshCw className="h-5 w-5" />}
            onClick={fetchUsers}
          >
            Actualizar
          </Button>
          <Button
            variant="primary"
            icon={<UserPlus className="h-5 w-5" />}
            onClick={handleAddUser}
          >
            Nuevo Usuario
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader title="Usuarios del Sistema" />
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fecha de Creación
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {editingUser === user.id ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={editingRole}
                              onChange={(e) => setEditingRole(e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            >
                              <option value="Administrador">Administrador</option>
                              <option value="Vendedor">Vendedor</option>
                              <option value="Producción">Producción</option>
                              <option value="Depósito">Depósito</option>
                            </select>
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Save className="h-4 w-4" />}
                              onClick={() => saveEditRole(user.id)}
                              disabled={isLoading}
                            >
                              Guardar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<X className="h-4 w-4" />}
                              onClick={cancelEditRole}
                              disabled={isLoading}
                            >
                              Cancelar
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'Administrador' 
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200' 
                                : user.role === 'Vendedor'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
                                : user.role === 'Producción'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                            }`}>
                              {user.role}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Edit className="h-4 w-4" />}
                              onClick={() => startEditRole(user)}
                              disabled={isLoading}
                            >
                              Editar
                            </Button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(user.created_at).toLocaleDateString('es-AR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Key className="h-4 w-4" />}
                            onClick={() => handleResetPassword(user)}
                          >
                            Contraseña
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Trash2 className="h-4 w-4" />}
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        No hay usuarios registrados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleUserSaved}
        initialData={selectedUser}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        title="Eliminar Usuario"
        message="¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />

      {/* Password Reset Modal */}
      <PasswordResetModal
        isOpen={isPasswordResetModalOpen}
        onClose={() => {
          setIsPasswordResetModalOpen(false);
          setUserToReset(null);
        }}
        user={userToReset}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => {
          setIsErrorModalOpen(false);
          setError(null);
        }}
        message={error || 'Ha ocurrido un error inesperado'}
      />

      {/* Toast Component */}
      {ToastComponent}
    </div>
  );
};

export default UserManagement;